import { DEFAULT_OUTLINE_NUMBERING } from '@govinity/shared-config';
import { z } from 'zod/v4';
import { AGENDA_DOCX_TEMPLATE_TYPES } from '../agenda-docx-templates/index.js';
import { defaultActionMustBeAvailableRefinement, hasValidDefaultAction, } from '../legislative-actions/index.js';
import { numberingStyleSchema } from '../shared/index.js';
export const agendaTemplateSectionItemSchema = z.object({
    itemTypeId: z.number().int().min(1, 'Item type is required'),
    fullTitle: z.string().min(1, 'Full title is required'),
});
const agendaTemplateSectionCreateBaseSchema = z.object({
    id: z
        .number()
        .optional()
        .describe('Existing section ID. Omit for new sections; include to update an existing one.'),
    uuid: z.string().optional(),
    title: z.string().min(1, 'Title is required').max(255).describe('Required for create'),
    description: z.string().nullish(),
    displayType: z
        .string()
        .min(1, 'Display type is required')
        .max(255)
        .describe('"always" or "conditional". Conditional sections hide when they contain no items.'),
    orderById: z
        .number()
        .min(1, 'Order by is required')
        .describe('1-based position within the parent section list.'),
    automaticAssignmentRules: z
        .string()
        .nullish()
        .describe('Natural-language instructions used by the AI auto-assigner to decide which legislative items belong in this section.'),
    acceptsItems: z.boolean().describe('Whether legislative items can be added to this section.'),
    isConsentSection: z
        .boolean()
        .optional()
        .describe('Whether this is a consent-calendar section. Items in a consent section are approved together by a single section vote rather than individually.'),
    restartsNumbering: z
        .boolean()
        .optional()
        .describe('Whether auto-numbering restarts at this section: it is numbered 1 (A, I, …) again and its following siblings continue from there. Copied onto the matching agenda section when an agenda is created from the template.'),
    allowPublicSpeakers: z
        .boolean()
        .optional()
        .describe('Whether the public can sign up to speak on this section. Copied onto the matching agenda section when an agenda is created from the template.'),
    allowItemPublicSpeakers: z
        .boolean()
        .optional()
        .describe('Whether the public can sign up to speak on the legislative items within this section. Independent of allowPublicSpeakers, and copied onto the matching agenda section when an agenda is created from the template.'),
    allowPublicComments: z
        .boolean()
        .optional()
        .describe('Whether the public can submit written comments on this section. Copied onto the matching agenda section when an agenda is created from the template.'),
    allowItemPublicComments: z
        .boolean()
        .optional()
        .describe('Whether the public can submit written comments on the legislative items within this section. Independent of allowPublicComments, and copied onto the matching agenda section when an agenda is created from the template.'),
    displayChildSectionsFirst: z
        .boolean()
        .optional()
        .describe('Whether child sections are displayed before the legislative items of this section. Copied onto the matching agenda section when an agenda is created from the template.'),
    recommendedActionText: z
        .string()
        .nullish()
        .describe('Suggested motion/recommendation language shown on items in this section.'),
    actionIds: z
        .array(z.number())
        .optional()
        .describe('IDs of legislative actions available on items in this section.'),
    defaultActionId: z
        .number()
        .nullish()
        .describe('Default legislative action ID. Must be one of actionIds.'),
    items: z
        .array(agendaTemplateSectionItemSchema)
        .optional()
        .describe('Pre-populated items that auto-create when the section is added to an agenda.'),
});
export const agendaTemplateSectionCreateSchema = agendaTemplateSectionCreateBaseSchema.refine(hasValidDefaultAction, defaultActionMustBeAvailableRefinement);
export const agendaTemplateSectionCreateSchemaWithChildren = agendaTemplateSectionCreateBaseSchema
    .extend({
    childSections: z.array(agendaTemplateSectionCreateBaseSchema
        .extend({
        childSections: z
            .array(agendaTemplateSectionCreateBaseSchema
            .extend({
            childSections: z
                .array(agendaTemplateSectionCreateBaseSchema
                .extend({
                childSections: z.array(agendaTemplateSectionCreateSchema).optional(),
            })
                .refine(hasValidDefaultAction, defaultActionMustBeAvailableRefinement))
                .optional(),
        })
            .refine(hasValidDefaultAction, defaultActionMustBeAvailableRefinement))
            .optional(),
    })
        .refine(hasValidDefaultAction, defaultActionMustBeAvailableRefinement)),
})
    .refine(hasValidDefaultAction, defaultActionMustBeAvailableRefinement);
export const ITEM_NUMBERING_MODES = ['per_section', 'continuous'];
// Outline numbering level schema
export const outlineNumberingLevelSchema = z.object({
    format: z.string().min(1, 'Format is required'),
    style: numberingStyleSchema,
    includeFrom: z
        .union([
        z.enum(['current_level_only', 'level_1_onwards', 'level_2_onwards', 'level_3_onwards']),
        z.string().regex(/^level_\d+_onwards$/, 'Must follow pattern: level_X_onwards'),
    ])
        .optional(),
});
// Outline numbering configuration schema
export const outlineNumberingConfigSchema = z.object({
    levels: z.record(z.string(), outlineNumberingLevelSchema),
    itemNumbering: z
        .enum(ITEM_NUMBERING_MODES)
        .optional()
        .describe('How the level under the top-level sections is counted. "per_section" (default) restarts the count in every section; "continuous" runs one sequence across the whole agenda.'),
    autoRenumber: z
        .boolean()
        .optional()
        .describe('Whether agendas renumber themselves whenever items or sections are added, removed or moved, instead of waiting for Auto Number. Numbers typed by hand are kept. Defaults to false.'),
});
/**
 * A config is valid with any subset of levels, so external callers can store one that only covers
 * levels 1 and 2. Missing levels (and other missing settings) fall back to the system default
 * rather than rendering blank numbers, and every stored config ends up covering all four levels.
 */
export const withDefaultOutlineNumberingLevels = (config) => ({
    levels: { ...DEFAULT_OUTLINE_NUMBERING.levels, ...config?.levels },
    itemNumbering: config?.itemNumbering ?? DEFAULT_OUTLINE_NUMBERING.itemNumbering,
    autoRenumber: config?.autoRenumber ?? DEFAULT_OUTLINE_NUMBERING.autoRenumber,
});
export const agendaTemplateDocxTemplateSchema = z.object({
    id: z.number().int().min(1, 'Docx template is required'),
    type: z.enum(AGENDA_DOCX_TEMPLATE_TYPES),
});
export const agendaTemplateDocxTemplatesSchema = z
    .array(agendaTemplateDocxTemplateSchema)
    .refine((docxTemplates) => {
    const agendaIds = docxTemplates.filter(({ type }) => type === 'agenda').map(({ id }) => id);
    return new Set(agendaIds).size <= 1;
}, 'Only one agenda Word template can be selected');
export const agendaTemplateCreateSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255).describe('Required for create'),
    title: z.string().min(1, 'Title is required').max(255).describe('Required for create'),
    description: z.string().min(1, 'Description cannot be empty').nullish(),
    header: z.string().nullish().describe('HTML header content shown above the agenda body.'),
    footer: z.string().nullish().describe('HTML footer content shown below the agenda body.'),
    isActive: z.boolean().describe('Whether the template is available for selection.'),
    agendaPublicationRuleId: z
        .number()
        .int()
        .positive()
        .nullish()
        .describe('Publication rule applied to agendas created from this template. Omit or send null to track no publish deadline.'),
    docxTemplates: agendaTemplateDocxTemplatesSchema
        .optional()
        .describe('Full set of Word docx templates this agenda template uses, each tagged with its type. At most one "agenda" entry; any number of "minutes" entries. Replaces every existing link on update, so send the complete list.'),
    outlineNumbering: outlineNumberingConfigSchema
        .optional()
        .describe('Outline numbering config, keyed by level ("1" through "4"). Omit it — or any individual level — to use the system default for what is missing.'),
    sections: z
        .array(agendaTemplateSectionCreateSchemaWithChildren)
        .describe('Full ordered list of top-level sections. On update, sections not included here are deleted — fetch the current template first if making partial edits.'),
});
// Export the default outline numbering configuration
export { DEFAULT_OUTLINE_NUMBERING } from '@govinity/shared-config';
